// Device Packaging → Distribution: show validation/readiness, Back vs Proceed.

import React, { JSX, useCallback, useEffect, useMemo, useState } from 'react';
import type { EventDeviceRow } from '../types';
import type { DeviceStatus } from '../../../../types/DeviceListTypes';
import InlineSpinner from '../../shared/InlineSpinner';
import BarcodeScannerEntryField from '../../shared/BarcodeScannerEntryField';
import useBarcodeScanner from '../../shared/useBarcodeScanner';
import { deviceIdMatchesTableFilter } from '../../shared/DeviceIdTableFilterBar';
import { maybeConvertRacerangerScan } from '../../../helpers/racerangerDeviceScan';
import EventStatusTransitionDialogFrame from './EventStatusTransitionDialogFrame';
import { AthleteAssignmentReportTable } from '../../shared/AthleteAssignmentReportTable';
import {
  buildRaceValidationReportCsv,
  RaceValidationReportTable,
} from '../../shared/RaceValidationReportTable';
import type { RaceValidationReportRow } from '../../shared/types/ValidationReportTypes';
import RaceTransitionVerificationPanel, {
  VerificationSummaryCaution,
  VerificationSummaryOk,
  VerificationSummaryWarn,
} from '../../shared/RaceTransitionVerificationPanel';
import {
  fetchAllEventDevicesRaw,
  formatApiResponseError,
  type RaceConfigurationRequirements,
} from '../utils';
import VerificationTableActions, {
  copyVerificationCsvToClipboard,
  downloadVerificationCsv,
} from '../../shared/VerificationTableActions';
import VerificationTableExpandDialog from '../../shared/VerificationTableExpandDialog';
import {
  athleteMatchesTableFilter,
  buildAthleteAssignmentReportCsv,
  buildAthleteAssignmentReportRows,
} from '../utils/athleteAssignmentReport';
import {
  getDeviceValidationBreakdown,
  getDeviceValidationBreakdownWhenRaceConfigurationMissing,
  getDeviceValidationMismatchReasons,
  isDeviceSubjectToFleetValidation,
  isRaceConfigurationReadyForDeviceVerification,
  RACE_CONFIGURATION_REQUIRED_FOR_VERIFICATION_MESSAGE,
} from '../../../helpers/deviceValidation';
import { buildRaceValidationReportRows } from '../../../helpers/raceValidationReportRows';
import { pairedFrontIdFromRear } from '../../../helpers/devicePairing';
import {
  computePackagingToDistributionReadinessChecks,
  isPackagingToDistributionFullyReady,
  mapLifecycleRowsByDeviceId,
  mapStatusRowsToDeviceStatusById,
  parseRaceConfigurationFromApiResponse,
  parseAthletesCsv,
  type AthleteCsvRow,
} from '../utils';
import {
  parseRaceAthletesApiResponse,
  raceAthletesToPackageAssignmentRows,
} from '../utils/raceAthletes';
import {
  areAllPackagingCommandStepsComplete,
  readPackagingCommandChecklist,
} from '../utils/raceDeviceCommandChecklist';

type ValidationRow = {
  deviceId: string;
  /** Device's assigned race (from event device list); used to resolve race configuration. */
  raceId?: string;
  mismatchReasons: string[];
  lifecycleRow?: Record<string, unknown>;
  status?: DeviceStatus;
};

/** First blocking issue when assigned devices cannot be checked against a saved race configuration. */
function raceConfigurationBlockingMessage(
  rows: Pick<ValidationRow, 'raceId'>[],
  raceConfigSummaries: { raceId: string; cfg: RaceConfigurationRequirements | null; loadOk: boolean }[],
): string | null {
  for (const r of rows) {
    const rid = String(r.raceId ?? '').trim();
    if (!rid) {
      return 'One or more devices are not assigned to a race, so configuration cannot be verified.';
    }
    const s = raceConfigSummaries.find((x) => x.raceId === rid);
    if (!s || !s.loadOk) {
      return 'Race configuration could not be loaded.';
    }
    if (!String(s.cfg?.fw_version ?? '').trim() || !String(s.cfg?.fw_version_front ?? '').trim()) {
      return RACE_CONFIGURATION_REQUIRED_FOR_VERIFICATION_MESSAGE;
    }
  }
  return null;
}

type PackagingToDistributionConfirmDialogProps = {
  open: boolean;
  eventId: string;
  eventName?: string;
  /** When set, only devices assigned to this race are validated and listed. */
  restrictToRaceId?: string | null;
  /** Shown under the title (e.g. race name). */
  subtitleOverride?: string;
  busy: boolean;
  onCancel: () => void;
  onProceed: () => void | Promise<void>;
};

export default function PackagingToDistributionConfirmDialog(
  props: PackagingToDistributionConfirmDialogProps,
): JSX.Element | null {
  const {
    open,
    eventId,
    eventName,
    restrictToRaceId = null,
    subtitleOverride,
    busy,
    onCancel,
    onProceed,
  } = props;

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [raceConfigSummaries, setRaceConfigSummaries] = useState<
    { raceId: string; cfg: RaceConfigurationRequirements | null; loadOk: boolean }[]
  >([]);
  const [rows, setRows] = useState<ValidationRow[]>([]);
  const [assignmentRowsForReadiness, setAssignmentRowsForReadiness] = useState<EventDeviceRow[]>([]);
  const [packagingCommandChecklist, setPackagingCommandChecklist] = useState(
    () => readPackagingCommandChecklist(eventId, restrictToRaceId ?? ''),
  );
  const [athleteListRows, setAthleteListRows] = useState<AthleteCsvRow[]>([]);
  const [deviceIdFilter, setDeviceIdFilter] = useState('');
  const [athleteFilter, setAthleteFilter] = useState('');
  const [athleteFilterEntryMode, setAthleteFilterEntryMode] = useState<'scan' | 'manual'>('scan');
  const [deviceFilterEntryMode, setDeviceFilterEntryMode] = useState<'scan' | 'manual'>('scan');
  const [verificationScanTarget, setVerificationScanTarget] = useState<'athlete' | 'device'>('athlete');
  const [expandedVerification, setExpandedVerification] = useState<'athlete' | 'device' | null>(null);

  const verificationScannerEnabled =
    open &&
    !loading &&
    !busy &&
    ((verificationScanTarget === 'athlete' && athleteFilterEntryMode === 'scan' && athleteListRows.length > 0) ||
      (verificationScanTarget === 'device' && deviceFilterEntryMode === 'scan' && rows.length > 0));

  const {
    connectionActive: filterScannerConnected,
    connectionPort: filterScannerPort,
    claim: claimFilterScanner,
  } = useBarcodeScanner({
    enabled: verificationScannerEnabled,
    pollIntervalMs: 1500,
    mapScan: (raw) => {
      const trimmed = String(raw ?? '').trim();
      if (!trimmed) return null;
      if (verificationScanTarget === 'device') {
        return maybeConvertRacerangerScan(trimmed);
      }
      return trimmed;
    },
    onScan: (value) => {
      if (verificationScanTarget === 'athlete') {
        setAthleteFilter(value);
        return;
      }
      setDeviceIdFilter(value);
    },
  });

  useEffect(() => {
    if (!open) {
      setDeviceIdFilter('');
      setAssignmentRowsForReadiness([]);
      setPackagingCommandChecklist(readPackagingCommandChecklist(eventId, restrictToRaceId ?? ''));
      setAthleteListRows([]);
      setAthleteFilter('');
      setAthleteFilterEntryMode('scan');
      setDeviceFilterEntryMode('scan');
      setVerificationScanTarget('athlete');
      setExpandedVerification(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (athleteListRows.length > 0) {
      setVerificationScanTarget('athlete');
    } else if (rows.length > 0) {
      setVerificationScanTarget('device');
    }
  }, [open, athleteListRows.length, rows.length]);

  useEffect(() => {
    if (!open || !eventId.trim()) {
      setLoading(false);
      setLoadError(null);
      setRaceConfigSummaries([]);
      setRows([]);
      setAthleteListRows([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setRaceConfigSummaries([]);
    setRows([]);
    setAthleteListRows([]);

    void (async () => {
      try {
        const api = window.eventsAPI as
          | {
            getRaceConfiguration?: (p: { eventId: string; raceId: string }) => Promise<unknown>;
            queryEventDeviceStatuses?: (eventId: string, deviceIds: string[]) => Promise<unknown>;
            listRaceAthletes?: (eventId: string, raceId: string) => Promise<unknown>;
            getAthletesCsv?: (eventId: string, options?: { raceId?: string }) => Promise<unknown>;
          }
          | undefined;

        if (!api?.getRaceConfiguration || !api?.queryEventDeviceStatuses) {
          throw new Error('Events API is not available');
        }

        const devRes = await fetchAllEventDevicesRaw(
          eventId,
          restrictToRaceId?.trim() ? { raceId: restrictToRaceId.trim() } : undefined,
        );
        if (cancelled) return;

        const rawDevices = (devRes.devices ?? []) as { deviceId?: string; raceId?: string }[];
        const mappedRows: EventDeviceRow[] = rawDevices
          .map((d) => {
            const row = d as Record<string, unknown>;
            return {
              deviceId: String(row.deviceId ?? '').trim(),
              raceId: String(row.raceId ?? '').trim() || undefined,
              assignedAthleteId:
                String(row.assignedAthleteId ?? row.assigned_athlete_id ?? '').trim() || undefined,
              assignedAthleteName:
                String(row.assignedAthleteName ?? row.assigned_athlete_name ?? '').trim() || undefined,
              assignedAt: undefined,
              assignedBy: undefined,
              recalled:
                row.recalled != null && row.recalled !== ''
                  ? (Number(row.recalled) as 0 | 1)
                  : undefined,
            };
          })
          .filter((r) => r.deviceId);
        setAssignmentRowsForReadiness(mappedRows);

        const restrict = restrictToRaceId?.trim();
        let loadedRosterFromDynamo = false;
        if (restrict && api.listRaceAthletes) {
          const raceAthletesRes = await api.listRaceAthletes(eventId, restrict);
          if (cancelled) return;
          const raceAthleteRows = raceAthletesToPackageAssignmentRows(
            parseRaceAthletesApiResponse((raceAthletesRes as { data?: unknown } | null | undefined)?.data),
          );
          if (raceAthleteRows.length > 0) {
            setAthleteListRows(raceAthleteRows);
            loadedRosterFromDynamo = true;
          }
        }
        if (restrict && !loadedRosterFromDynamo && api.getAthletesCsv) {
          const athletesRes = await api.getAthletesCsv(eventId, { raceId: restrict });
          if (cancelled) return;
          const ar = athletesRes as { ok?: boolean; data?: { csvText?: unknown } } | null | undefined;
          if (ar?.ok && typeof ar.data?.csvText === 'string') {
            const parsed = parseAthletesCsv(ar.data.csvText);
            if (!parsed.error) {
              setAthleteListRows(parsed.rows);
            }
          }
        }

        const deviceRaceById: Record<string, string> = {};
        const deviceIds: string[] = [];
        for (const d of rawDevices) {
          const did = String(d?.deviceId ?? '').trim();
          if (!did) continue;
          deviceIds.push(did);
          const rid = String(d?.raceId ?? '').trim();
          if (rid) deviceRaceById[did] = rid;
        }

        const uniqueRaceIdsSet = new Set(Object.values(deviceRaceById));
        if (restrict) uniqueRaceIdsSet.add(restrict);
        const uniqueRaceIds = Array.from(uniqueRaceIdsSet).sort();

        const cfgByRaceId: Record<string, RaceConfigurationRequirements | null> = {};
        const summaries: { raceId: string; cfg: RaceConfigurationRequirements | null; loadOk: boolean }[] = [];

        await Promise.all(
          uniqueRaceIds.map(async (raceId) => {
            const raceRes = await api.getRaceConfiguration!({ eventId, raceId });
            if (cancelled) return;
            const { cfg, loadOk } = parseRaceConfigurationFromApiResponse(raceRes);
            cfgByRaceId[raceId] = cfg;
            summaries.push({ raceId, cfg, loadOk });
          }),
        );
        if (cancelled) return;

        summaries.sort((a, b) => a.raceId.localeCompare(b.raceId));
        setRaceConfigSummaries(summaries);

        if (deviceIds.length === 0) {
          setRows([]);
          return;
        }

        const [stRes, lifecycleRes] = await Promise.all([
          api.queryEventDeviceStatuses(eventId, deviceIds),
          window.eventsAPI?.getDeviceLifecycle
            ? window.eventsAPI.getDeviceLifecycle({ deviceIds })
            : Promise.resolve({ ok: false, data: { devices: [] } }),
        ]);
        const statusRes = stRes as {
          ok?: boolean;
          data?: { statuses?: unknown[] };
          status?: number;
          error?: string;
        };
        const lifecycleRows = Array.isArray((lifecycleRes as any)?.data?.devices)
          ? (lifecycleRes as any).data.devices
          : [];
        if (cancelled) return;
        if (!statusRes?.ok) {
          setLoadError(formatApiResponseError(statusRes, 'Failed to load device statuses'));
          setRows([]);
          return;
        }

        const statusRows = Array.isArray(statusRes.data?.statuses) ? statusRes.data!.statuses! : [];
        const statusById = mapStatusRowsToDeviceStatusById(statusRows);
        const lifecycleById = mapLifecycleRowsByDeviceId(lifecycleRows);
        const validationNowMs = Date.now();
        const nextRows: ValidationRow[] = deviceIds.map((deviceId) => {
          const status = statusById[deviceId];
          const lifecycleRow = lifecycleById[deviceId];
          const rid = deviceRaceById[deviceId] || '';
          const raceCfg = rid ? cfgByRaceId[rid] ?? null : null;
          const pairedFrontId = pairedFrontIdFromRear(deviceId);
          const frontStatus = pairedFrontId ? statusById[pairedFrontId] : undefined;
          const mismatchReasons = isDeviceSubjectToFleetValidation(deviceId)
            ? getDeviceValidationMismatchReasons(raceCfg, status, lifecycleRow, validationNowMs, frontStatus)
            : [];
          return { deviceId, raceId: rid || undefined, mismatchReasons, lifecycleRow, status };
        });
        nextRows.sort((a, b) => {
          const aBad = a.mismatchReasons.length > 0 ? 0 : 1;
          const bBad = b.mismatchReasons.length > 0 ? 0 : 1;
          if (aBad !== bBad) return aBad - bBad;
          return a.deviceId.localeCompare(b.deviceId);
        });
        setRows(nextRows);
      } catch (err: unknown) {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : 'Failed to load verification data');
          setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, eventId, restrictToRaceId]);

  useEffect(() => {
    if (!open) return;
    const rid = restrictToRaceId?.trim();
    if (!rid) return;
    setPackagingCommandChecklist(readPackagingCommandChecklist(eventId, rid));
  }, [open, eventId, restrictToRaceId]);

  useEffect(() => {
    if (!open) return;
    const rid = restrictToRaceId?.trim();
    if (!rid) return;
    const onChecklistChanged = (): void => {
      setPackagingCommandChecklist(readPackagingCommandChecklist(eventId, rid));
    };
    window.addEventListener('fleet-packaging-command-checklist-changed', onChecklistChanged);
    return () => window.removeEventListener('fleet-packaging-command-checklist-changed', onChecklistChanged);
  }, [open, eventId, restrictToRaceId]);

  const mismatchCount = useMemo(() => rows.filter((r) => r.mismatchReasons.length > 0).length, [rows]);

  const raceConfigBlock = useMemo(() => {
    const detail = raceConfigurationBlockingMessage(rows, raceConfigSummaries);
    return { ok: detail == null, detail };
  }, [rows, raceConfigSummaries]);

  const readinessGateChecks = useMemo(() => {
    if (!open || loading || loadError || !restrictToRaceId?.trim()) return null;
    const rid = restrictToRaceId.trim();
    const meta = raceConfigSummaries.find((s) => s.raceId === rid);
    if (!meta) return null;
    const statusByDeviceId: Record<string, DeviceStatus> = {};
    const lifecycleByDeviceId: Record<string, unknown> = {};
    for (const r of rows) {
      if (r.status) statusByDeviceId[r.deviceId] = r.status;
      lifecycleByDeviceId[r.deviceId] = r.lifecycleRow ?? {};
    }
    return computePackagingToDistributionReadinessChecks({
      displayRows: assignmentRowsForReadiness,
      raceConfig: meta.cfg,
      raceConfigLoadOk: meta.loadOk,
      statusByDeviceId,
      lifecycleByDeviceId,
    });
  }, [
    open,
    loading,
    loadError,
    restrictToRaceId,
    raceConfigSummaries,
    rows,
    assignmentRowsForReadiness,
  ]);

  const readinessGatesPass =
    readinessGateChecks != null && isPackagingToDistributionFullyReady(readinessGateChecks);
  const packagingCommandChecksPass = areAllPackagingCommandStepsComplete(packagingCommandChecklist);
  const hasNonCommandReadinessFailures = Boolean(
    readinessGateChecks?.some((c) => !c.ok && c.blocking !== false),
  );

  const primaryRaceConfigMeta = useMemo(() => {
    const rid = restrictToRaceId?.trim();
    if (!rid) return { cfg: null as RaceConfigurationRequirements | null, loadOk: false };
    const summary = raceConfigSummaries.find((s) => s.raceId === rid);
    return { cfg: summary?.cfg ?? null, loadOk: summary?.loadOk ?? false };
  }, [restrictToRaceId, raceConfigSummaries]);

  const raceConfigReadyForVerification = useMemo(
    () =>
      isRaceConfigurationReadyForDeviceVerification(
        primaryRaceConfigMeta.cfg,
        primaryRaceConfigMeta.loadOk,
      ),
    [primaryRaceConfigMeta],
  );

  const deviceValidationReportRows = useMemo((): RaceValidationReportRow[] => {
    const deviceRefs = assignmentRowsForReadiness.map((d) => ({
      deviceId: String(d.deviceId ?? '').trim(),
      assignedAthleteId: d.assignedAthleteId,
    })).filter((d) => d.deviceId);

    const statusByDeviceId: Record<string, ValidationRow['status']> = {};
    const lifecycleByDeviceId: Record<string, ValidationRow['lifecycleRow']> = {};
    for (const row of rows) {
      statusByDeviceId[row.deviceId] = row.status;
      lifecycleByDeviceId[row.deviceId] = row.lifecycleRow;
    }

    const validationNowMs = Date.now();
    return buildRaceValidationReportRows({
      devices: deviceRefs,
      getBreakdown: (deviceId, frontDeviceId) => {
        if (!raceConfigReadyForVerification) {
          return getDeviceValidationBreakdownWhenRaceConfigurationMissing();
        }
        return getDeviceValidationBreakdown(
          primaryRaceConfigMeta.cfg,
          statusByDeviceId[deviceId],
          lifecycleByDeviceId[deviceId],
          validationNowMs,
          frontDeviceId ? statusByDeviceId[frontDeviceId] : undefined,
        );
      },
    });
  }, [rows, assignmentRowsForReadiness, primaryRaceConfigMeta.cfg, raceConfigReadyForVerification]);

  const deviceVerificationIssueCount = useMemo(() => {
    if (!raceConfigReadyForVerification) return deviceValidationReportRows.length;
    return deviceValidationReportRows.filter((r) => r.breakdown.notes).length;
  }, [raceConfigReadyForVerification, deviceValidationReportRows]);

  const filteredDeviceValidationRows = useMemo(
    () => deviceValidationReportRows.filter(
      (r) => deviceIdMatchesTableFilter(r.deviceId, deviceIdFilter)
        || (r.frontDeviceId ? deviceIdMatchesTableFilter(r.frontDeviceId, deviceIdFilter) : false),
    ),
    [deviceValidationReportRows, deviceIdFilter],
  );

  const athleteAssignmentReportRows = useMemo(
    () => buildAthleteAssignmentReportRows(athleteListRows, assignmentRowsForReadiness),
    [athleteListRows, assignmentRowsForReadiness],
  );

  const missingRearAthleteCount = useMemo(
    () => athleteAssignmentReportRows.filter((r) => r.rear.result === 'fail').length,
    [athleteAssignmentReportRows],
  );
  const missingFrontAthleteCount = useMemo(
    () => athleteAssignmentReportRows.filter((r) => r.front.result === 'fail').length,
    [athleteAssignmentReportRows],
  );
  const athleteAssignmentIssueCount = missingRearAthleteCount + missingFrontAthleteCount;

  const filteredAthleteAssignmentRows = useMemo(
    () => athleteAssignmentReportRows.filter((r) => athleteMatchesTableFilter(r, athleteFilter)),
    [athleteAssignmentReportRows, athleteFilter],
  );

  const allAthletesAssigned =
    athleteAssignmentReportRows.length > 0 && athleteAssignmentIssueCount === 0;

  const allDevicesConform =
    !loading &&
    !loadError &&
    rows.length > 0 &&
    raceConfigReadyForVerification &&
    deviceVerificationIssueCount === 0 &&
    raceConfigBlock.ok;

  const raceIdForExport = restrictToRaceId?.trim() ?? '';

  const athleteAssignmentCsv = useMemo(
    () =>
      buildAthleteAssignmentReportCsv({
        eventId,
        raceId: raceIdForExport,
        rows: athleteAssignmentReportRows,
      }),
    [eventId, raceIdForExport, athleteAssignmentReportRows],
  );

  const deviceValidationCsv = useMemo(
    () =>
      buildRaceValidationReportCsv({
        eventId,
        raceId: raceIdForExport,
        rows: deviceValidationReportRows,
      }),
    [eventId, raceIdForExport, deviceValidationReportRows],
  );

  if (!open) return null;

  const titleName =
    (subtitleOverride?.trim() ? subtitleOverride.trim() : null) ||
    (eventName ? eventName : eventId);

  const exportScopeSubtitle = [eventId, raceIdForExport, titleName].filter(Boolean).join(' · ');

  const verificationFilterInputClass = [
    'w-full h-10 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900',
    'text-sm text-gray-900 dark:text-slate-100 shadow-sm transition-shadow',
    'focus:outline-none focus:ring-2 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-blue-500/20 dark:focus:ring-blue-500/30',
  ].join(' ');

  const scannerStatusHint = filterScannerConnected
    ? `Scanner connected${filterScannerPort?.path ? ` (${filterScannerPort.path})` : ''}.`
    : 'Connect a barcode scanner, or switch to Manual to type.';

  return (
    <EventStatusTransitionDialogFrame
      open={open}
      busy={busy}
      onCancel={onCancel}
      onProceed={onProceed}
      wide
      extraWide
      titleId="packaging-to-distribution-title"
      eyebrow="Race distribution"
      title="Move to Distribution"
      subtitle={titleName}
      headerAccent={allDevicesConform ? 'emerald' : 'amber'}
      confirmPhrase="DISTRIBUTION"
      confirmInputId="packaging-to-distribution-confirm-input"
      proceedButtonText="Proceed to Distribution"
      proceedDisabledExtra={loading || !packagingCommandChecksPass}
      proceedUseSuccessTone={allDevicesConform && readinessGatesPass}
    >
      {!packagingCommandChecksPass ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-950 dark:border-amber-700/60 dark:bg-amber-950/40 dark:text-amber-100">
          <div className="font-semibold text-amber-950 dark:text-amber-100">Device commands not complete</div>
          <p className="mt-1 leading-relaxed text-amber-900/90 dark:text-amber-200/90">
            Open this race from the table (Device Packaging workspace) and tick every item under Device commands before
            proceeding. Validation issues below can still be overridden after you type DISTRIBUTION.
          </p>
        </div>
      ) : null}

      {readinessGateChecks && hasNonCommandReadinessFailures ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-950 dark:border-amber-700/60 dark:bg-amber-950/40 dark:text-amber-100">
          <div className="font-semibold text-amber-950 dark:text-amber-100">Some recommended checks are not passing</div>
          <p className="mt-1 leading-relaxed text-amber-900/90 dark:text-amber-200/90">
            You can proceed to Distribution if you accept the risk. Type DISTRIBUTION below to continue.
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-4 text-amber-900 dark:text-amber-200">
            {readinessGateChecks
              .filter((c) => !c.ok)
              .map((c) => (
                <li key={c.key}>
                  <span className="font-medium">{c.label}</span>
                  {c.detail ? <span className="text-amber-800/90 dark:text-amber-200/80"> — {c.detail}</span> : null}
                </li>
              ))}
          </ul>
        </div>
      ) : null}

      {loading ? (
        <div className="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-3 text-sm text-gray-600 dark:text-slate-300 shadow-sm">
          <InlineSpinner />
          <span>Checking assigned devices against race configuration…</span>
        </div>
      ) : null}

      {!loading && athleteAssignmentReportRows.length > 0 ? (
        <RaceTransitionVerificationPanel
          title="Athlete assignment verification"
          tone={allAthletesAssigned ? 'ok' : 'warn'}
          itemCount={athleteAssignmentReportRows.length}
          itemLabel="athlete"
          issueCount={athleteAssignmentIssueCount}
          tableMaxHeightClass="max-h-72"
          headerActions={
            <VerificationTableActions
              rowCount={athleteAssignmentReportRows.length}
              disabled={busy}
              onExpand={() => setExpandedVerification('athlete')}
              onCopyCsv={() =>
                copyVerificationCsvToClipboard(athleteAssignmentCsv, 'Athlete assignment report CSV')
              }
              onDownloadCsv={() =>
                downloadVerificationCsv(
                  athleteAssignmentCsv,
                  raceIdForExport
                    ? `athlete-assignments-${eventId}-${raceIdForExport}`
                    : `athlete-assignments-${eventId}`,
                )
              }
            />
          }
          summary={
            allAthletesAssigned ? (
              <VerificationSummaryOk>Every athlete on the athlete list has rear and front devices assigned.</VerificationSummaryOk>
            ) : (
              <VerificationSummaryWarn>
                {missingRearAthleteCount > 0 ? (
                  <>
                    {missingRearAthleteCount} athlete{missingRearAthleteCount === 1 ? '' : 's'} do not have a rear
                    device assigned.
                  </>
                ) : null}
                {missingRearAthleteCount > 0 && missingFrontAthleteCount > 0 ? ' ' : null}
                {missingFrontAthleteCount > 0 ? (
                  <>
                    {missingFrontAthleteCount} athlete{missingFrontAthleteCount === 1 ? '' : 's'} do not have a front
                    device assigned.
                  </>
                ) : null}
                {' '}THESE ATHLETES WILL BE REMOVED FROM DISTRIBUTION.
              </VerificationSummaryWarn>
            )
          }
          filter={
            <>
              <label className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                Filter athletes
              </label>
              <p className="text-[11px] text-gray-500 dark:text-slate-400">{scannerStatusHint}</p>
              <BarcodeScannerEntryField
                connected={filterScannerConnected}
                value={athleteFilter}
                onChange={setAthleteFilter}
                onSubmit={(v) => setAthleteFilter(v.trim())}
                disabled={busy}
                className={verificationFilterInputClass}
                mode={athleteFilterEntryMode}
                onModeChange={setAthleteFilterEntryMode}
                onFocus={() => {
                  setVerificationScanTarget('athlete');
                  claimFilterScanner();
                }}
                placeholderScan="Scan race number or search athlete name or ID"
                placeholderManual="Search athlete name or ID"
              />
            </>
          }
        >
          <AthleteAssignmentReportTable
            rows={filteredAthleteAssignmentRows}
            emptyMessage={
              athleteAssignmentReportRows.length === 0
                ? 'No athletes on the athlete list.'
                : 'No athletes match the filter.'
            }
          />
        </RaceTransitionVerificationPanel>
      ) : null}

      {!loading && rows.length > 0 ? (
        <RaceTransitionVerificationPanel
          title="Device verification"
          tone={allDevicesConform ? 'ok' : deviceVerificationIssueCount > 0 ? 'warn' : 'neutral'}
          itemCount={rows.length}
          itemLabel="device"
          issueCount={deviceVerificationIssueCount}
          tableMaxHeightClass="max-h-[32rem]"
          headerActions={
            <VerificationTableActions
              rowCount={deviceValidationReportRows.length}
              disabled={busy}
              onExpand={() => setExpandedVerification('device')}
              onCopyCsv={() =>
                copyVerificationCsvToClipboard(deviceValidationCsv, 'Device validation report CSV')
              }
              onDownloadCsv={() =>
                downloadVerificationCsv(
                  deviceValidationCsv,
                  raceIdForExport
                    ? `device-validation-${eventId}-${raceIdForExport}`
                    : `device-validation-${eventId}`,
                )
              }
            />
          }
          summary={
            !raceConfigReadyForVerification ? (
              <VerificationSummaryCaution detail={RACE_CONFIGURATION_REQUIRED_FOR_VERIFICATION_MESSAGE} />
            ) : deviceVerificationIssueCount > 0 ? (
              <VerificationSummaryWarn>
                update devices or race configuration, then try again. You can still proceed if you accept the risk.
              </VerificationSummaryWarn>
            ) : !raceConfigBlock.ok && raceConfigBlock.detail ? (
              <VerificationSummaryCaution detail={raceConfigBlock.detail} />
            ) : (
              <VerificationSummaryOk>
                All assigned devices meet the current race validation requirements.
              </VerificationSummaryOk>
            )
          }
          filter={
            <>
              <label className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                Filter by device ID
              </label>
              <p className="text-[11px] text-gray-500 dark:text-slate-400">{scannerStatusHint}</p>
              <BarcodeScannerEntryField
                connected={filterScannerConnected}
                value={deviceIdFilter}
                onChange={setDeviceIdFilter}
                onSubmit={(v) => setDeviceIdFilter(v.trim())}
                disabled={busy}
                className={verificationFilterInputClass}
                mode={deviceFilterEntryMode}
                onModeChange={setDeviceFilterEntryMode}
                onFocus={() => {
                  setVerificationScanTarget('device');
                  claimFilterScanner();
                }}
                parseValue={maybeConvertRacerangerScan}
                placeholderScan="Scan device ID"
                placeholderManual="Type device ID"
              />
            </>
          }
        >
          <RaceValidationReportTable
            rows={filteredDeviceValidationRows}
            eventId={eventId}
            notesDisabled={busy}
          />
        </RaceTransitionVerificationPanel>
      ) : !loading && !loadError ? (
        <p className="rounded-lg border border-dashed border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 px-4 py-3 text-center text-xs text-gray-500 dark:text-slate-400">
          No devices assigned to this race yet.
        </p>
      ) : null}

      <VerificationTableExpandDialog
        open={expandedVerification === 'athlete'}
        onClose={() => setExpandedVerification(null)}
        title="Athlete assignment verification"
        subtitle={exportScopeSubtitle}
        metaLine={`${athleteAssignmentReportRows.length} athlete${athleteAssignmentReportRows.length === 1 ? '' : 's'}, ${missingRearAthleteCount} without rear, ${missingFrontAthleteCount} without front`}
        csvText={athleteAssignmentCsv}
        downloadFilenameBase={
          raceIdForExport
            ? `athlete-assignments-${eventId}-${raceIdForExport}`
            : `athlete-assignments-${eventId}`
        }
        csvToastLabel="Athlete assignment report CSV"
      >
        <AthleteAssignmentReportTable rows={athleteAssignmentReportRows} />
      </VerificationTableExpandDialog>

      <VerificationTableExpandDialog
        open={expandedVerification === 'device'}
        onClose={() => setExpandedVerification(null)}
        title="Device verification"
        subtitle={exportScopeSubtitle}
        metaLine={`${deviceValidationReportRows.length} device${deviceValidationReportRows.length === 1 ? '' : 's'}, ${deviceVerificationIssueCount} issue${deviceVerificationIssueCount === 1 ? '' : 's'}`}
        csvText={deviceValidationCsv}
        downloadFilenameBase={
          raceIdForExport
            ? `device-validation-${eventId}-${raceIdForExport}`
            : `device-validation-${eventId}`
        }
        csvToastLabel="Device validation report CSV"
      >
        <RaceValidationReportTable
          rows={deviceValidationReportRows}
          eventId={eventId}
          notesDisabled={busy}
        />
      </VerificationTableExpandDialog>
    </EventStatusTransitionDialogFrame>
  );
}
